import { Action, configureStore, EnhancedStore } from "@reduxjs/toolkit";
import { ThunkAction } from "redux-thunk";
import { setupListeners } from "@reduxjs/toolkit/query/react";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { rootReducer, RootState } from "../reducers/index";
import { registerApi } from "../reducers/registerapi";
import {
    FLUSH,
    PAUSE,
    PERSIST,
    Persistor,
    persistReducer,
    persistStore,
    PURGE,
    REGISTER,
    REHYDRATE,
} from "redux-persist";
import storage from "redux-persist/lib/storage"; // defaults to localStorage for web

// 2. Create a persist config
const persistConfig = {
    key: "root", // key for the top-level state
    storage, // define which storage to use
    whitelist: ["cart", "checkout"], // list state slices you want to persist (example)
};

const persistedReducer = persistReducer<ReturnType<typeof rootReducer>>(
    persistConfig,
    rootReducer,
);

export const createStore = (): {
    store: EnhancedStore;
    persistor: Persistor; // typically from redux-persist, or you can define a type
} => {
    const store = configureStore({
        reducer: persistedReducer,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                // 4. Ignore redux-persist actions in serializableCheck
                serializableCheck: {
                    ignoredActions: [
                        FLUSH,
                        REHYDRATE,
                        PAUSE,
                        PERSIST,
                        PURGE,
                        REGISTER,
                    ],
                },
            }).concat(registerApi.middleware),
    });

    const persistor = persistStore(store);

    setupListeners(store.dispatch);

    return { store, persistor };
};

export type AppDispatch = ReturnType<typeof createStore>["store"]["dispatch"];
export type AppThunk = ThunkAction<void, RootState, unknown, Action<string>>;
export const useAppDispatch = () => useDispatch<AppDispatch>(); // Export a hook that can be reused to resolve types
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
